import React, { FC, RefObject, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { useMemoizedFn, useSize } from 'ahooks';
import { crabDevice } from '@hfzq/crab-utils';
import { BottomInset, TopInset } from '../app-body/content-inset';
import styles from './index.module.less';

type TopDistanceType = number | (() => number);

interface StickyInBodyProps {
  position?: 'top' | 'bottom';
  children?: React.ReactNode;
  topDistance?: TopDistanceType;
  topInset?: boolean;
  zIndex?: number;
  topContentClassName?: string;
  stickyTopClassName?: string;
  onStatusChange?: (isSticky: boolean) => void;
  disableBottomInset?: boolean;
}

export const StickyInBody: FC<StickyInBodyProps> = ({
  children,
  position = 'top',
  zIndex = 1,
  topInset = false,
  topDistance = 0,
  topContentClassName,
  stickyTopClassName,
  onStatusChange,
  disableBottomInset,
}) => {
  if (position === 'top') {
    return (
      <StickyInBodyTop
        contentClassName={topContentClassName}
        topDistance={topDistance}
        inset={topInset}
        zIndex={zIndex}
        onStatusChange={onStatusChange}
        stickyClassName={stickyTopClassName}
      >
        {children}
      </StickyInBodyTop>
    );
  }
  return (
    <StickyInBodyBottom disableBottomInset={disableBottomInset} zIndex={zIndex}>
      {children}
    </StickyInBodyBottom>
  );
};

type StickyInBodyTopProps = {
  children?: React.ReactNode;
  zIndex: number;
  inset: boolean;
  topDistance: TopDistanceType;
  contentClassName?: string;
  stickyClassName?: string;
  onStatusChange?: (isSticky: boolean) => void;
};

const StickyInBodyTop: FC<StickyInBodyTopProps> = ({
  children,
  zIndex,
  inset,
  topDistance,
  contentClassName,
  stickyClassName,
  onStatusChange,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const placeHolderRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const size = useSize(ref);
  const [isSticky, top] = useStickyTopInBody(placeHolderRef, topDistance);
  const height = size?.height !== undefined ? (size?.height || 0) - 1 : undefined;
  const handleStatusChange = useMemoizedFn((state: boolean) => {
    onStatusChange?.(state);
  });

  const forceRepaint = () => {
    if (!crabDevice.isIOS) {
      return;
    }
    if (!containerRef.current) {
      return;
    }
    const ele = containerRef.current;
    const displayStyle = ele.style.display;
    ele.style.display = 'none';
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const _ = ele.offsetHeight;
    ele.style.display = displayStyle;
  };

  React.useEffect(() => {
    handleStatusChange(isSticky);
  }, [handleStatusChange, isSticky]);

  React.useEffect(() => {
    forceRepaint();
  }, [isSticky]);

  return (
    <div style={{ width: '100%' }} ref={containerRef}>
      <div ref={placeHolderRef} style={{ height: isSticky ? height : 0, width: '100%' }} />
      <div
        style={{ height, overflow: 'hidden', zIndex, top }}
        className={classNames(
          contentClassName,
          isSticky && stickyClassName,
          isSticky && styles.sticky,
          styles.stickyTop
        )}
      >
        <div ref={ref}>
          {inset && <TopInset />}
          {children}
        </div>
      </div>
    </div>
  );
};

type StickyInBodyBottomProps = {
  children?: React.ReactNode;
  zIndex: number;
  disableBottomInset?: boolean;
};

const StickyInBodyBottom: FC<StickyInBodyBottomProps> = ({ children, zIndex, disableBottomInset = false }) => {
  const ref = useRef<HTMLDivElement>(null);
  const size = useSize(ref);
  return (
    <div style={{ width: '100%' }}>
      <div style={{ height: size?.height || 0, width: '100%' }} />
      <div ref={ref} className={classNames(styles.sticky, styles.stickyBottom)} style={{ zIndex }}>
        {children}
        {!disableBottomInset && <BottomInset style={{ backgroundColor: 'white' }} />}
      </div>
    </div>
  );
};

const useStickyTopInBody = (ele: RefObject<HTMLDivElement>, topDistance: TopDistanceType) => {
  const [isSticky, setSticky] = useState(false);
  const [top, setTop] = useState(0);

  const handleScroll = useCallback(() => {
    const scrollTop = document.scrollingElement?.scrollTop || 0;
    if (scrollTop + top >= getElementTop(ele.current as HTMLElement)) {
      setSticky(true);
    } else {
      setSticky(false);
    }
  }, [ele, top]);

  useLayoutEffect(() => {
    setTop(topDistance);
  }, [topDistance]);

  useLayoutEffect(() => {
    document.addEventListener('scroll', handleScroll);
    return () => document.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  // 修复渲染时短时间闪烁问题
  useEffect(() => {
    document.body.addEventListener('touchstart', handleScroll);
    return () => {
      document.body.removeEventListener('touchstart', handleScroll);
    };
  }, [handleScroll]);

  return [isSticky, top] as const;
};

/**
 * 计算元素距离顶部高度
 * @param el 元素
 * @returns 距离顶部的高度
 */
function getElementTop(el: HTMLElement) {
  if (!el) {
    return 0;
  }
  let actualTop = el.offsetTop;
  let current = el.offsetParent;
  while (current !== null) {
    actualTop += (current as HTMLElement).offsetTop;
    current = (current as HTMLElement).offsetParent;
  }
  return actualTop;
}
